import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FunctionComponent, useCallback, useId, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Switch from '@rio-cloud/rio-uikit/Switch';

export const isXmlString = (value: string): boolean => {
    const trimmedValue = value.trim();

    if (!trimmedValue.startsWith('<') || !trimmedValue.endsWith('>')) {
        return false;
    }

    try {
        const parsedValue = new DOMParser().parseFromString(trimmedValue, 'application/xml');
        return parsedValue.querySelector('parsererror') === null;
    } catch {
        return false;
    }
};

export const XmlResult: FunctionComponent<{ rawXmlString: string }> = ({ rawXmlString }) => {
    const switchInputId = useId();
    const [showDialog, setShowDialog] = useState(false);
    const [prettyPrint, setPrettyPrint] = useState<boolean>(false);
    const formattedXmlString = useMemo(() => {
        return prettyPrint ? formatXml(rawXmlString) : rawXmlString;
    }, [rawXmlString, prettyPrint]);

    const onCloseDialog = useCallback(() => {
        setShowDialog(false);
        setPrettyPrint(false);
    }, [setShowDialog, setPrettyPrint]);

    const dialogBody = (
        // The intention here is that the dialog body doesn't overflow in any direction. The overflow
        // of the XML content should only happen in the <pre> element to ensure both scroll bars
        // (horizontal and vertical) are always on screen. If the vertical scrolling happens in the
        // dialog body but the horizontal scrolling in the <pre> element a user needs to first scroll
        // to the bottom to see the horizontal scroll bar.
        <div className="height-100pct width-100pct display-flex flex-column">
            <label
                htmlFor={switchInputId}
                className="display-flex justify-content-between align-items-center border rounded bg-lightest padding-10"
            >
                <FormattedMessage id="outboundOrderBook.inspector.resulttable.xml.format.label" />
                <Switch keyName={switchInputId} checked={prettyPrint} onChange={setPrettyPrint} />
            </label>
            <pre role="code" className="margin-top-20 flex-1-1 json-code bg-lightest rounded">
                {formattedXmlString}
            </pre>
        </div>
    );

    return (
        <>
            <Button iconName="rioglyph-code-brackets" onClick={() => setShowDialog(true)}>
                <FormattedMessage id="outboundOrderBook.common.button.show" />
            </Button>
            <Dialog
                show={showDialog}
                bsSize={Dialog.SIZE_FULL_HEIGHT}
                title={<FormattedMessage id="outboundOrderBook.inspector.resulttable.xml.title" />}
                body={dialogBody}
                footer={
                    <button type="button" className="btn btn-primary" onClick={onCloseDialog}>
                        <FormattedMessage id="outboundOrderBook.common.button.close" />
                    </button>
                }
                onClose={onCloseDialog}
                showCloseButton
            />
        </>
    );
};

// The repo does not currently include an XML formatting dependency.
// This lightweight formatter is enough for inspector display purposes because
// values are already validated as XML before reaching this path, and we only
// need stable line breaks/indentation for preview and dialog rendering.
const formatXml = (rawXml: string): string => {
    const tabWidth = 2;
    const normalizedValue = rawXml.trim().replace(/>\s*</g, '><');
    const xmlLines = normalizedValue.replace(/></g, '>\n<').split('\n');
    let indentationLevel = 0;

    return xmlLines
        .map((line) => {
            const trimmedLine = line.trim();
            const isClosingTag = /^<\//.test(trimmedLine);
            const isDeclaration = /^<\?/.test(trimmedLine);
            const isComment = /^<!--/.test(trimmedLine);
            const isOpeningTag =
                /^<[^!?/][^>]*>$/.test(trimmedLine) && !/\/>$/.test(trimmedLine) && !trimmedLine.includes('</');

            if (isClosingTag && indentationLevel > 0) {
                indentationLevel -= 1;
            }

            const formattedLine = `${' '.repeat(tabWidth * indentationLevel)}${trimmedLine}`;

            if (isOpeningTag && !isDeclaration && !isComment) {
                indentationLevel += 1;
            }

            return formattedLine;
        })
        .join('\n');
};
